import { Shape } from "../screens/Settings/RampsSettings/RampEditor/RampCanvas/RampCanvas";
import { Spot } from "./../screens/Ramp/TemporalTest/types";
import {
  anchorAircraftToPoint,
  getAircraftKeyPositions,
} from "./../screens/RampOptimization/algo/placement2/util";
import { Tenant } from "./../types";
import {
  convertPointTo3sf,
  Coordinate,
  getDistanceBetweenCoords,
  getLineClockAngle,
  rotateAroundOrigin,
} from "./coordinates";
import { getBoundingBox, getPolygonOverlapAreaSize } from "./geometry";
import {
  convertDegreesToRadians,
  convertRadiansToDegress,
  getDifferenceBetweenAngles,
  roundTo3sf,
} from "./math";

export type ReferencePointName = "north" | "east" | "south" | "west" | "center";

/**
 * Given a spot which is a box, this enforces the coordinates being in the
 * correct order that's required for `computeReferencePointPosition`.
 * However this can only be used if the box edges are parrallel to the x and
 * y axis, you can't use this on a rotated spot.
 * Assumes the current origin is bottom left. And if the coordinates
 * are not in the order South West, South East, North East, then North West
 * it will throw an error.
 */
export const assertCornerPositionsAreNorthFacingOnPerpendicularBox = function (
  spotBoundary: Coordinate[]
) {
  var p1 = convertPointTo3sf(spotBoundary[0]);
  var p2 = convertPointTo3sf(spotBoundary[1]);
  var p3 = convertPointTo3sf(spotBoundary[2]);
  var p4 = convertPointTo3sf(spotBoundary[3]);

  var xCoords = [p1.x, p2.x, p3.x, p4.x];
  xCoords = xCoords.filter((o, i) => i == xCoords.lastIndexOf(o));
  var yCoords = [p1.y, p2.y, p3.y, p4.y];
  yCoords = yCoords.filter((o, i) => i == yCoords.lastIndexOf(o));

  if (xCoords.length > 2 || yCoords.length > 2) {
    throw new Error("Passed in box is not parallel to the x and y axes.");
  }

  var minX = Math.min(...xCoords);
  var maxX = Math.max(...xCoords);
  var minY = Math.min(...yCoords);
  var maxY = Math.max(...yCoords);

  var sw = { x: minX, y: minY };
  var se = { x: maxX, y: minY };
  var ne = { x: maxX, y: maxY };
  var nw = { x: minX, y: maxY };

  if (
    !(
      sw.x == p1.x &&
      sw.y == p1.y &&
      se.x == p2.x &&
      se.y == p2.y &&
      ne.x == p3.x &&
      ne.y == p3.y &&
      nw.x == p4.x &&
      nw.y == p4.y
    )
  ) {
    throw new Error(
      "Coordinates are not in the correct order, i.e. South West, South East, North East, then North West."
    );
  }
};

/**
 * @param spotBoundary 4 coordinates marking the rectangular area of a spot.
 * The coordiantes should be in this order South West, South East, North East,
 * then North West (this is based off the sample spot file).
 * The origin of the coordinate system is bottom left of the screen.
 */
export const computeReferencePointPosition = function (
  spotBoundary: Coordinate[],
  referencePointName: ReferencePointName
): Coordinate {
  // original was inverted
  var sw = spotBoundary[0];
  var se = spotBoundary[1];
  var ne = spotBoundary[2];
  var nw = spotBoundary[3];

  switch (referencePointName) {
    case "north":
      return { x: nw.x + (ne.x - nw.x) / 2, y: nw.y + (ne.y - nw.y) / 2 };
    case "east":
      return { x: se.x + (ne.x - se.x) / 2, y: se.y + (ne.y - se.y) / 2 };
    case "south":
      return { x: sw.x + (se.x - sw.x) / 2, y: sw.y + (se.y - sw.y) / 2 };
    case "west":
      return { x: sw.x + (nw.x - sw.x) / 2, y: sw.y + (nw.y - sw.y) / 2 };
    case "center":
      return { x: sw.x + (ne.x - sw.x) / 2, y: sw.y + (ne.y - sw.y) / 2 };
    default:
      throw new Error(`Unknown referencePointName ${referencePointName}.`);
  }
};

export type ReferencePoint = {
  name: ReferencePointName;
  validOrientations: OrientationType[];
};

export type OrientationType = "nose_to_line" | "tail_to_line" | "center";

const getSpotClockAngle = function (points) {
  // The spot clock angle would be the equivilent of the rotation on the
  // west line
  return convertRadiansToDegress(getLineClockAngle([points[3], points[0]]));
};

type GetPositionForSpotOptions = {
  /**
   * Used as a tool to help visualise what is going on in the function, it
   * can be used to draw on an element to visualise geometry operations.
   * There's no reason to populate this in production, it's a development
   * only option.
   */
  domLayer?: Element;
  /**
   * Similar to the domLayer this is only used in development, it's used so
   * we can map feet values to the equivilant pixels to draw on the domLayer.
   * There's no reason to populate this in production.
   */
  feetToPixels?: number;
  /**
   * By default we check if an aircraft fits inside a box, if it doesn't then
   * for what could be an otherwise valid position we would return undefined
   * instead. For example, if this is set to false and an aircraft is larger
   * than the box but we have a matching orientation to a spot reference point
   * then that aircraft position will still be returned even though the aircraft
   * is larger than the box. Another good example would be if the aircraft is
   * slightly smaller than the box but it's anchored on the nose in the center
   * of the box, this check will ensure undefined is returned for that position.
   * However if you are doing pre calculation on whether or not aircraft fit
   * inside a box you can set this to false to reduce expensive computation.
   */
  checkAircraftFitsInBox?: boolean;

  /**
   * When using a coordinate system where the origin is top left, set this
   * to true. It has more repucussions than just inverting the coordinate
   * system because it means when 'south' is passed in that has a different
   * meaning.
   */
  yAxisInverted?: boolean;

  /**
   * This is only needed as a debug prop. When using the yAxisInverted: true
   * property we need to know the height/depth of the ramp so that we know
   * where to draw our visualisations to aid debugging. But using a value
   * of 0 will still result in the correct end result.
   */
  rampDepth?: number;
};

type AircraftPosition = {
  x: number;
  y: number;
  angle: number;
};

export type SpotAssignmentResult = {
  spot: LegacySpot;
  referencePointName: ReferencePointName;
  orientation: OrientationType;
};

/**
 * Given a spot return the Aircraft position to match the specified orientation.
 * However if the requested orientation isn't valid for the spot then
 * undefined is returned
 */
export const getPositionForSpot = function getPositionForSpot(
  spot: Spot,
  rampShapes: Shape[],
  orientation: OrientationType,
  tenant: Tenant,
  options: GetPositionForSpotOptions = {}
): AircraftPosition | undefined {
  var spotShape = rampShapes.find((s) => s.id === spot.id && s.type === "Spot");
  if (!spotShape) {
    console.warn(`Spot shape ${spot.id} not found in ramp shapes`);
    // TODO Should throw instead?
    return undefined;
  }

  return getPositionForSpotLegacy(
    {
      points: spotShape.points,
      referencePoints: spotShape.reference_points || [],
    },
    orientation,
    tenant,
    options
  );
};

type LegacySpot = {
  points: Coordinate[];
  referencePoints: ReferencePoint[];
};

/**
 * The getPositionForSpot with the old structure.
 */
export const getPositionForSpotLegacy = function getPositionForSpotLegacy(
  spot: LegacySpot,
  orientation: OrientationType,
  tenant: Tenant,
  options: GetPositionForSpotOptions = {}
): AircraftPosition | undefined {
  // The "orientation" value  functions as filter criteria for our available
  // reference positions, maybe in the future we'll add other filter criteria
  // like "north", "center", etc
  var referencePoints = spot.referencePoints.filter((o) => {
    return o.validOrientations.includes(orientation);
  });

  // Out of our remaining reference points that are applicable let's check if
  // any work, and by work, we mean that the aircraft fits inside it when
  // anchored via the specified orientation.
  for (var referencePoint of referencePoints) {
    var temp = getPositionForSingleSpotGeometry(
      spot.points,
      referencePoint.name,
      orientation,
      tenant,
      options
    );
    // The only way this will ne undefined is if the option
    // `checkAircraftFitsInBox` is set to true and in that scenario we check
    // the next valid option
    if (temp) return temp;
  }
};

/**
 * This is the same as `getPositionForSpotLegacy` however there's a core
 * distinction, a spot can contain multiple valid reference points however
 * this function accepts only one reference point and one orientation.
 */
export const getPositionForSingleSpotGeometry =
  function getPositionForSingleSpotGeometry(
    points: Coordinate[],
    referencePointName: ReferencePointName,
    orientation: OrientationType,
    tenant: Tenant,
    options: GetPositionForSpotOptions = {}
  ): AircraftPosition | undefined {
    const {
      checkAircraftFitsInBox = true,
      yAxisInverted,
      rampDepth = 0,
    } = options;

    //var drawLine = createDrawLine(document.getElementById('aircraftDebugDrawLayer'))
    //var drawPolygon = createDrawPolygon(drawLine)

    // If we're working on an inverted y axis then nip in the bud everything
    // that needs to be different here so that we don't have to handle
    // differences deeper in the stack
    if (yAxisInverted) {
      points = points.map((o) => ({ y: rampDepth - o.y, x: o.x }));
      // We also need to change the order of points else our clock angle is
      // going to be wrong
      // South West, South East, North East, then North West
      points = [points[3], points[2], points[1], points[0]];
    }

    //drawPolygon(spot.points.map(coord => scaleCoord(coord, feetToPixels)), 'red')

    // Let's get the angle of our spot, we'll use this later to know which
    // way the aircraft should face
    var spotClockAngle = getSpotClockAngle(points);

    // We'll next rotate our box so north is always facing up on our coordinate
    // system, this will make doing our calculations a lot simpler, and when
    // we're done, we'll rotate back
    var northFacingSpotPoints = points.map((p) =>
      rotateAroundOrigin(p, -convertDegreesToRadians(spotClockAngle), points[0])
    );
    // Lets assert that our coordinates are in the right order because if they
    // are not we'll see weird positioning behaviour and create a very hard
    // to track down bug, so its better to throw now than suffer positioning bugs
    assertCornerPositionsAreNorthFacingOnPerpendicularBox(
      northFacingSpotPoints
    );

    var northFacingAnchorPoint = computeReferencePointPosition(
      northFacingSpotPoints,
      referencePointName
    );
    //drawDot(scaleCoord(northFacingAnchorPoint, feetToPixels), 'pink')

    // Maps to the value our algo function uses, TODO we could refactor this out
    // by making them both use the same
    let anchorOn: "center" | "nose" | "tail" | "true_nose" | "true_tail";
    let aircraftClockAngle: number;
    switch (orientation) {
      case "center":
        anchorOn = "center";
        // When parking in the center position of a spot, we're assuming the
        // aircraft will be parked facing north, in the future maybe we could
        // expand the validOrientations to also include the parking angle
        aircraftClockAngle = 0;
        break;
      case "nose_to_line":
        anchorOn = "true_nose";
        aircraftClockAngle = 0;
        break;
      case "tail_to_line":
        anchorOn = "true_tail";
        // On tail to line and when aligned on the North edge the aircraft will
        // be pointing south
        aircraftClockAngle = 180;
        break;
      default:
        throw new Error(`Unknown orientation '${orientation}'.`);
    }

    // Based on our reference name let's adjust our a aircraft angle to face
    // the direction of which side of the box we're aligning on
    // TODO Maybe we could refactor these directions out by changing the
    // rotation step at the start
    switch (referencePointName) {
      case "east":
        aircraftClockAngle += 90;
        break;
      case "south":
        aircraftClockAngle += 180;
        break;
      case "west":
        aircraftClockAngle += 270;
        break;
      // No adjustments needed
      case "center":
      case "north":
        break;
      default:
        throw new Error(`Unknown reference point name ${referencePointName}.`);
    }
    if (checkAircraftFitsInBox) {
      var northFaceingAircraftPosition = anchorAircraftToPoint({
        aircraft: tenant.aircraft,
        anchorPosition: northFacingAnchorPoint,
        aircraftClockAngle,
        anchorOn,
      });

      var northFaceingAircraftGeom = getAircraftKeyPositions(
        {
          aircraft: tenant.aircraft,
          aircraftOriginPosition: northFaceingAircraftPosition,
          aircraftClockAngle,
        }
        //feetToPixels,
      ).aircraftGeom;
      // There is an inaccuracy in the getAircraftKeyPositions, it could be
      // related to feetToPixel conversions but I don't want to refactor it
      // at this point because it works, but it might end up with a coordinate
      // that is 0.01 out from the exact value. And due to this, when checking
      // if the aircraft fits inside the box it will be ever so slightly out
      // on the anchor side. Or it could be slightly out due to a different
      // unknown reason.
      // But given we are anchoring on a specific edge it would be valid to assume
      // that if the aircraft goes over that edge when we anchor it on a point
      // we have told it to anchor on then we will still want to treat this
      // as valid so let's cap those positions at the respective
      // edges.
      // For example if aligning on the north edge at the center of the aircraft
      // it will overlap the edge, but that's what was asked of the algorithm to
      // do so it makes sense to be valid
      var boundingBox = getBoundingBox(northFacingSpotPoints);
      // Mutates the aircraft polygon coordinates and clips them at the anchor
      // edge
      northFaceingAircraftGeom.forEach((o) => {
        switch (referencePointName) {
          case "east":
            o.x = Math.min(o.x, boundingBox.maxX);
            break;
          case "south":
            o.y = Math.max(o.y, boundingBox.minY);
            break;
          case "west":
            o.x = Math.max(o.x, boundingBox.minX);
            break;
          case "north":
            o.y = Math.min(o.y, boundingBox.maxY);
            break;
          default:
            return o;
        }
      });

      /*
    var northFacingAircraftGeomPixels = northFaceingAircraftGeom.map(o => ({
      x: o.x * feetToPixels,
      y: o.y * feetToPixels,
    }))
    drawPolygon(northFacingAircraftGeomPixels, 'lightblue')*/

      // This is our check that it's a valid position because if we do something
      // like anchoring on the nose in the center of the box we don't want
      // to return this position if half the aircraft is outside of the box
      // and there's subtler scenarios, for example an aircraft at 45° might
      // fit it's wings inside a box whereas it might not at 90°, and while
      // we don't support that yet we're doing the check so that we don't get
      // caught out in the future
      var aircraftFitsInSpot = doesPolygonFitInAxesAlignedBox(
        northFaceingAircraftGeom.map(convertPointTo3sf),
        northFacingSpotPoints.map(convertPointTo3sf)
      );

      if (!aircraftFitsInSpot) {
        return;
      }
    }

    // The position is valid but we need to move it back to be aligned
    // with where the box was originally, i.e. not parallel to the coordinate
    // axes
    // So let's rotate our anchor point back and then calculate the position
    // based on that
    //drawDot(scaleCoord(northFaceingAircraftPosition, feetToPixels), 'yellow')
    //drawDot(scaleCoord(spot.points[0], feetToPixels), 'lightgreen')
    var anchorPoint = rotateAroundOrigin(
      northFacingAnchorPoint,
      convertDegreesToRadians(spotClockAngle),
      points[0]
    );
    //drawDot(scaleCoord(anchorPoint, feetToPixels), 'pink')

    var aircraftAngle = (aircraftClockAngle + spotClockAngle + 360) % 360;

    var aircraftPosition = anchorAircraftToPoint({
      aircraft: tenant.aircraft,
      anchorPosition: anchorPoint,
      aircraftClockAngle: aircraftAngle,
      anchorOn,
    });

    // Test it to make sure we've got the right position
    /*var aircraftGeom = getAircraftKeyPositions(
    {
      aircraft: tenant.aircraft, 
      aircraftOriginPosition: aircraftPosition, 
      aircraftClockAngle: aircraftAngle,
    }, 
    //feetToPixels,
  ).aircraftGeom

  var aircraftGeomPixels = aircraftGeom.map(o => ({
    x: o.x * feetToPixels,
    y: o.y * feetToPixels,
  }))
  drawPolygon(aircraftGeomPixels, 'purple')*/

    if (yAxisInverted) {
      // Map back to what we're expecting
      aircraftPosition = {
        x: aircraftPosition.x,
        y: rampDepth - aircraftPosition.y,
      };
    }

    return {
      ...aircraftPosition,
      angle: aircraftAngle,
    };
  };

/**
 * With a list of passed in spots this function returns the spot that
 * best matches the aircraft position. If there's no spot that matches the
 * aircraft position then undefined is returned.
 */
export const getSpotForPositionLegacy = function getSpotForPositionLegacy(
  tenant: Tenant,
  aircraftPosition: AircraftPosition,
  spots: LegacySpot[],
  _options = {}
): SpotAssignmentResult | undefined {
  // Test all orientations
  /*for (var spot of spots){
    spot.referencePoints = [
      { "name": "north", "validOrientations": [ "nose_to_line", "tail_to_line", "center" ] },
      { "name": "east", "validOrientations": [ "nose_to_line", "tail_to_line", "center" ] },
      { "name": "south", "validOrientations": [ "nose_to_line", "tail_to_line", "center" ] },
      { "name": "west", "validOrientations": [ "nose_to_line", "tail_to_line", "center" ] },
      { "name": "center", "validOrientations": [ "nose_to_line", "tail_to_line", "center" ] },
    ]
  }*/

  //var drawLine = createDrawLine(document.getElementById('aircraftDebugDrawLayer'))
  //var drawPolygon = createDrawPolygon(drawLine)

  // For each spot calculate how much of the aircraft overlaps the spot
  // position and use the spot that overlaps the most

  var aircraftPolygon = getAircraftKeyPositions(
    {
      aircraft: tenant.aircraft,
      aircraftOriginPosition: { x: aircraftPosition.x, y: aircraftPosition.y },
      aircraftClockAngle: aircraftPosition.angle,
    }
    //feetToPixels,
  ).aircraftGeom;
  //drawPolygon(aircraftPolygon.map(coord => scaleCoord(coord, feetToPixels)), 'red')

  var largestOverlapSize;
  var closestMatchingSpot;
  for (var i = 0; i < spots.length; i++) {
    var spot = spots[i];
    var tempOverlapSize = getPolygonOverlapAreaSize(
      spot.points,
      aircraftPolygon
    );
    if (
      largestOverlapSize === undefined ||
      tempOverlapSize > largestOverlapSize
    ) {
      closestMatchingSpot = spot;
      largestOverlapSize = tempOverlapSize;
    }
  }

  // If the largest overlap size is 0 then we know there's no overlap
  if (!closestMatchingSpot || largestOverlapSize == 0) return undefined;

  // Iterate the orientations and grab the one that has the closest matching
  // angle to the existing aircraft angle
  var alignmentMatchDtos = [];
  for (var r of closestMatchingSpot.referencePoints) {
    for (var o of r.validOrientations) {
      var orientationAircraftAngle = getOrientationAircraftNoseAngle(
        closestMatchingSpot,
        r.name,
        o
      );
      var tempDiffBetweenAngles = getDifferenceBetweenAngles(
        orientationAircraftAngle,
        aircraftPosition.angle
      );
      // Gather details on all our alignments because we may have two identical
      // alignments, e.g. an aircraft fully contained within a spot
      // where one is nose_to_line facing east and the other is tail_to_line
      // facing west, both of these will have exactly the same angle
      // Of these matching alignments we'll check which one is closer to
      // the aircraft position
      alignmentMatchDtos.push({
        referencePoint: r,
        orientation: o,
        // Make the angles only to 3SF so that any JS rounding errors won't
        // give us the wrong result
        angleDifference: roundTo3sf(tempDiffBetweenAngles),
      });
    }
  }

  if (alignmentMatchDtos.length == 0) return undefined;

  var lowestAngle = alignmentMatchDtos[0].angleDifference;
  for (var i = 1; i < alignmentMatchDtos.length; i++) {
    var temp = alignmentMatchDtos[i];
    if (temp.angleDifference < lowestAngle) {
      lowestAngle = temp.angleDifference;
    }
  }

  var possibleOptions = alignmentMatchDtos.filter(
    (o) => o.angleDifference == lowestAngle
  );

  // If there's only one possible option then there's no need to do any expensive
  // compute
  if (possibleOptions.length == 1) {
    //drawPolygon(closestMatchingSpot.points.map(coord => scaleCoord(coord, feetToPixels)), 'lightblue')
    //drawLine.destroy()
    return {
      spot: closestMatchingSpot,
      referencePointName: possibleOptions[0].referencePoint.name,
      orientation: possibleOptions[0].orientation,
    };
  }

  // Let's compute how far away the aircraft is from our possible position
  // options
  var targetOption = possibleOptions[0];
  var targetOptionPosition = getPositionForSingleSpotGeometry(
    closestMatchingSpot.points,
    targetOption.referencePoint.name,
    targetOption.orientation,
    tenant
  );
  // if the position is undefined there's no point continuing. this happens when
  // the aircraft doesn't fit in the spot for that reference point + orientation
  if (!targetOptionPosition) {
    return undefined;
  }

  var targetOptionDistance = getDistanceBetweenCoords(
    { x: aircraftPosition.x, y: aircraftPosition.y },
    { x: targetOptionPosition!.x, y: targetOptionPosition!.y }
  );

  for (var i = 1; i < possibleOptions.length; i++) {
    var possibleOption = possibleOptions[i];
    /*var orientationAircraftAngle = getOrientationAircraftNoseAngle(
      closestMatchingSpot, r.name, o)*/

    //var drawLine2 = createDrawLine(document.getElementById('aircraftDebugDrawLayer'))
    //var drawPolygon2 = createDrawPolygon(drawLine)

    var position = getPositionForSingleSpotGeometry(
      closestMatchingSpot.points,
      possibleOption.referencePoint.name,
      possibleOption.orientation,
      tenant
    );

    var tempDistance = getDistanceBetweenCoords(
      { x: aircraftPosition.x, y: aircraftPosition.y },
      { x: position!.x, y: position!.y }
    );
    if (tempDistance < targetOptionDistance) {
      targetOption = possibleOption;
      targetOptionPosition = position;
      targetOptionDistance = tempDistance;
    }
    //drawLine2.destroy()
  }

  var referencePointName = targetOption.referencePoint.name;
  var orientation = targetOption.orientation;

  //drawPolygon(closestMatchingSpot.points.map(coord => scaleCoord(coord, feetToPixels)), 'lightblue')
  //debugger
  //drawLine.destroy()

  return {
    spot: closestMatchingSpot,
    referencePointName,
    orientation,
  };
};

/**
 * Check if a polygon fits inside a box. The box must be perpendicular to the
 * axes, if not you need to use a more robust polygon check.
 */
export function doesPolygonFitInAxesAlignedBox(polygon, axesAlignedBoxPoints) {
  var boundingBox = getBoundingBox(axesAlignedBoxPoints);
  return polygon.every((o) => {
    // Is point inside the box
    return (
      boundingBox.minX <= o.x &&
      o.x <= boundingBox.maxX &&
      boundingBox.minY <= o.y &&
      o.y <= boundingBox.maxY
    );
  });
}

/**
 * Given a spot, reference point and orientation this returns which angle
 * the nose of the aircraft would be facing.
 */
function getOrientationAircraftNoseAngle(
  spot: LegacySpot,
  referencePointName: ReferencePointName,
  orientationType: OrientationType
): number {
  var spotClockAngle = getSpotClockAngle(spot.points);

  switch (referencePointName) {
    case "east":
      spotClockAngle += 90;
      break;
    case "south":
      spotClockAngle += 180;
      break;
    case "west":
      spotClockAngle += 270;
      break;
    case "north":
    default:
  }

  switch (orientationType) {
    case "tail_to_line":
      spotClockAngle += 180;
      break;
    case "center":
    case "nose_to_line":
  }

  return spotClockAngle;
}
